import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import MediaInput from "../../components/admin/MediaInput";

const EMPTY = {
  title: "",
  subtitle: "",
  client: "",
  category: "Branding",
  year: new Date().getFullYear().toString(),
  cover_image: "",
  video_url: "",
  orientation: "horizontal",
  description: "",
  services: "",
  pull_quote: "",
  pull_quote_author: "",
  execution_blocks: [],
  gallery: [],
  featured: false,
  order: 0,
  // Teaser meta block
  meta_client_label: "Client",
  meta_client_visible: true,
  meta_year_label: "Year",
  meta_year_visible: true,
  meta_discipline_label: "Discipline",
  meta_discipline_visible: true,
  // Case study toggle + sections
  case_study_enabled: false,
  cs_client_label: "The Client",
  cs_client_name: "",
  cs_client_intro: "",
  cs_client_visible: true,
  cs_context_label: "The Situation",
  cs_context_body: "",
  cs_context_visible: true,
  cs_problem_label: "The Problem",
  cs_problem_body: "",
  cs_problem_visible: true,
  cs_insight_label: "The Insight",
  cs_insight_body: "",
  cs_insight_visible: true,
  cs_idea_label: "The Idea",
  cs_idea_body: "",
  cs_idea_visible: true,
  cs_execution_label: "Execution",
  cs_execution_visible: true,
  cs_keymoments_label: "Key Moments",
  cs_keymoments_intro: "",
  cs_keymoments_visible: true,
  key_moments: [],
  cs_outcome_label: "Outcome",
  cs_outcome_body: "",
  cs_outcome_visible: true,
  cs_closing_label: "Closing",
  cs_closing_body: "",
  cs_closing_visible: true,
};

export default function WorksAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = () => api.get("/works").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const customCats = (settings?.work_categories || []).filter(Boolean);
  const categoryOptions = customCats.length > 0 ? customCats : ["Branding", "Film", "Social", "Digital"];

  const save = async (form) => {
    if (form.id) {
      await api.put(`/works/${form.id}`, stripId(form));
    } else {
      await api.post("/works", stripId(form));
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this work permanently?")) return;
    await api.delete(`/works/${id}`);
    load();
  };

  return (
    <div data-testid="works-admin">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="overline mb-2">Portfolio</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Works</h1>
        </div>
        <button
          onClick={() => setEditing(EMPTY)}
          data-testid="new-work-btn"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-5 py-3 transition-all"
        >
          + New work
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((w) => (
          <div key={w.id} className="bg-[#121214] border border-white/5 overflow-hidden">
            <div className="aspect-[4/3] relative">
              <img src={w.cover_image} alt={w.title} className="absolute inset-0 w-full h-full object-cover" />
              {w.featured && (
                <span className="absolute top-3 left-3 overline bg-[#C9A050] text-black px-2 py-1 text-[9px]">Featured</span>
              )}
            </div>
            <div className="p-5">
              <p className="overline mb-2">{w.category} · {w.year}</p>
              <h3 className="font-display text-xl mb-4">{w.title}</h3>
              <div className="flex gap-3">
                <button onClick={() => setEditing(w)} className="overline text-white hover:text-[#C9A050]">Edit</button>
                <button onClick={() => remove(w.id)} className="overline text-[#8A8A93] hover:text-red-400">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <WorkForm initial={editing} onSave={save} onCancel={() => setEditing(null)} categoryOptions={categoryOptions} />
      )}
    </div>
  );
}

function stripId(f) {
  const { id, created_at, updated_at, ...rest } = f;
  return rest;
}

function WorkForm({ initial, onSave, onCancel, categoryOptions = ["Branding", "Film", "Social", "Digital"] }) {
  // ensure all defaults are present, even if `initial` is from a legacy record
  const merged = { ...EMPTY, ...initial };
  const [form, setForm] = useState(merged);
  const [galleryText, setGalleryText] = useState((merged.gallery || []).join("\n"));
  const [blocks, setBlocks] = useState(merged.execution_blocks || []);
  const [moments, setMoments] = useState(merged.key_moments || []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addBlock = (type) => setBlocks((b) => [...b, { type, url: "", caption: "" }]);
  const updateBlock = (idx, key, val) =>
    setBlocks((b) => b.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const removeBlock = (idx) => setBlocks((b) => b.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) =>
    setBlocks((b) => {
      const j = idx + dir;
      if (j < 0 || j >= b.length) return b;
      const next = [...b];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const addMoment = () =>
    setMoments((m) => [...m, { title: "", body: "", media_type: "image", media_url: "" }]);
  const updateMoment = (idx, key, val) =>
    setMoments((m) => m.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const removeMoment = (idx) => setMoments((m) => m.filter((_, i) => i !== idx));
  const moveMoment = (idx, dir) =>
    setMoments((m) => {
      const j = idx + dir;
      if (j < 0 || j >= m.length) return m;
      const next = [...m];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      order: Number(form.order) || 0,
      gallery: galleryText.split("\n").map((s) => s.trim()).filter(Boolean),
      execution_blocks: blocks.filter((b) => (b.url || "").trim()),
      key_moments: moments.filter((m) => (m.title || m.body || m.media_url || "").trim()),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur overflow-y-auto">
      <div className="max-w-3xl mx-auto my-12 bg-[#0A0A0C] border border-white/10 p-6 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-3xl">{form.id ? "Edit work" : "New work"}</h2>
          <button onClick={onCancel} className="overline text-[#8A8A93] hover:text-white">Cancel ×</button>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="work-form">
          <Field label="Title"><input required data-testid="work-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
          <Field label="Subtitle / one-line summary" hint="Shows directly under the title on the case study page. Keep it short and emotive."><input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} placeholder="A film about quiet morning rituals." /></Field>
          <Field label="Client"><input value={form.client} onChange={(e) => set("client", e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <input
                list="work-category-options"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className={inputCls}
                placeholder="Branding, Film, Social, Digital, or type your own"
                data-testid="work-category"
              />
              <datalist id="work-category-options">
                {categoryOptions.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="Year"><input value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Cover image" hint="Recommended: 1920 × 1080 (16:9 landscape), JPEG or WebP, ≤ 500KB. This exact ratio prevents any cropping on the Works grid and case-study hero.">
            <MediaInput
              value={form.cover_image}
              onChange={(v) => set("cover_image", v)}
              accept="image"
              folder="works"
              testid="work-cover"
              placeholder="https://…"
            />
          </Field>
          <Field label="Cover image — mobile (optional)" hint="Recommended: 1200 × 1600 (3:4 portrait), JPEG or WebP, ≤ 400KB. Use a portrait-friendly crop for phone viewports so the image never stretches. If left blank, the main cover image is used on mobile too.">
            <MediaInput
              value={form.cover_image_mobile || ""}
              onChange={(v) => set("cover_image_mobile", v)}
              accept="image"
              folder="works"
              testid="work-cover-mobile"
              placeholder="https://…"
            />
          </Field>
          <Field label="Hero Vimeo URL (case study)" hint="Full-bleed cinematic open. Vimeo URL only. Background plays muted, looped, no controls."><input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} placeholder="https://player.vimeo.com/video/…" /></Field>
          <Field label="Video / cover orientation" hint="Vertical (9:16) renders as a tall centered card on /work. Horizontal (16:9) uses the wide cinematic layout.">
            <select value={form.orientation || "horizontal"} onChange={(e) => set("orientation", e.target.value)} data-testid="work-orientation" className={inputCls}>
              <option value="horizontal">Horizontal — 16:9 (wide)</option>
              <option value="vertical">Vertical — 9:16 (tall, reels-style)</option>
            </select>
          </Field>
          <Field label="Brief / description" hint="Large editorial paragraph on the case study page."><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
          <Field label="Services" hint="Comma-separated. E.g. 'Brand identity, Direction, Sound design'."><input value={form.services || ""} onChange={(e) => set("services", e.target.value)} className={inputCls} placeholder="Direction, Cinematography, Edit" /></Field>

          {/* ─────────── Teaser meta block (editable labels + visibility) ─────────── */}
          <div className="pt-6 mt-6 border-t border-white/5">
            <label className="overline block mb-2">Teaser — meta block</label>
            <p className="text-xs text-[#8A8A93] mb-5">Each row on the teaser (right column) is editable and can be hidden per project. The Client value renders in gold.</p>
            <MetaRow form={form} set={set} field="meta_client" defaultLabel="Client" valueField="client" valueLabel="Client name (gold on teaser)" />
            <MetaRow form={form} set={set} field="meta_year" defaultLabel="Year" valueField="year" valueLabel="Year" />
            <MetaRow form={form} set={set} field="meta_discipline" defaultLabel="Discipline" valueField="category" valueLabel="Discipline / category" valueIsSelect />
          </div>

          {/* ─────────── Case study toggle ─────────── */}
          <div className="pt-6 mt-6 border-t border-white/5">
            <label className="flex items-center gap-3 mb-2">
              <input type="checkbox" checked={!!form.case_study_enabled} onChange={(e) => set("case_study_enabled", e.target.checked)} data-testid="cs-enabled" className="w-4 h-4 accent-[#C9A050]" />
              <span className="overline">Publish full case study</span>
            </label>
            <p className="text-xs text-[#8A8A93] ml-7">Turns on the “Read the full case study →” button on the teaser AND lists this project under <strong>/case-studies</strong>. Section content below is only visible when this is on.</p>
          </div>

          {form.case_study_enabled && (
            <div className="space-y-6 pt-2">
              {/* 1. Client */}
              <SectionEditor visible={form.cs_client_visible} onToggle={(v) => set("cs_client_visible", v)} label={form.cs_client_label} onLabelChange={(v) => set("cs_client_label", v)} testid="cs-client">
                <Field label="Client name (gold on detailed page; falls back to teaser ‘Client’ if empty)"><input value={form.cs_client_name || ""} onChange={(e) => set("cs_client_name", e.target.value)} className={inputCls} placeholder="Chote Kisan" /></Field>
                <Field label="About / intro"><textarea rows={3} value={form.cs_client_intro || ""} onChange={(e) => set("cs_client_intro", e.target.value)} className={inputCls} placeholder="Chote Kisan specialises in farm-to-table organic vegetables, supporting small farmers across the country." /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_context_visible} onToggle={(v) => set("cs_context_visible", v)} label={form.cs_context_label} onLabelChange={(v) => set("cs_context_label", v)} testid="cs-context">
                <Field label="Context body" hint="What was happening · Who is the client · Why this project mattered."><textarea rows={4} value={form.cs_context_body || ""} onChange={(e) => set("cs_context_body", e.target.value)} className={inputCls} /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_problem_visible} onToggle={(v) => set("cs_problem_visible", v)} label={form.cs_problem_label} onLabelChange={(v) => set("cs_problem_label", v)} testid="cs-problem">
                <Field label="Problem body" hint="What needed solving · What was at stake."><textarea rows={3} value={form.cs_problem_body || ""} onChange={(e) => set("cs_problem_body", e.target.value)} className={inputCls} /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_insight_visible} onToggle={(v) => set("cs_insight_visible", v)} label={form.cs_insight_label} onLabelChange={(v) => set("cs_insight_label", v)} testid="cs-insight">
                <Field label="Insight body" hint="What did you realise that others wouldn’t — your ‘brain moment’."><textarea rows={3} value={form.cs_insight_body || ""} onChange={(e) => set("cs_insight_body", e.target.value)} className={inputCls} /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_idea_visible} onToggle={(v) => set("cs_idea_visible", v)} label={form.cs_idea_label} onLabelChange={(v) => set("cs_idea_label", v)} testid="cs-idea">
                <Field label="Idea body" hint="Your creative direction · the core thought behind execution."><textarea rows={3} value={form.cs_idea_body || ""} onChange={(e) => set("cs_idea_body", e.target.value)} className={inputCls} /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_execution_visible} onToggle={(v) => set("cs_execution_visible", v)} label={form.cs_execution_label} onLabelChange={(v) => set("cs_execution_label", v)} testid="cs-execution-section">
                <ExecutionEditor blocks={blocks} addBlock={addBlock} updateBlock={updateBlock} removeBlock={removeBlock} moveBlock={moveBlock} />
              </SectionEditor>

              <SectionEditor visible={form.cs_keymoments_visible} onToggle={(v) => set("cs_keymoments_visible", v)} label={form.cs_keymoments_label} onLabelChange={(v) => set("cs_keymoments_label", v)} testid="cs-keymoments">
                <Field label="Intro paragraph (optional)"><textarea rows={2} value={form.cs_keymoments_intro || ""} onChange={(e) => set("cs_keymoments_intro", e.target.value)} className={inputCls} placeholder="Two moments stood out…" /></Field>
                <KeyMomentsEditor moments={moments} addMoment={addMoment} updateMoment={updateMoment} removeMoment={removeMoment} moveMoment={moveMoment} />
              </SectionEditor>

              <SectionEditor visible={form.cs_outcome_visible} onToggle={(v) => set("cs_outcome_visible", v)} label={form.cs_outcome_label} onLabelChange={(v) => set("cs_outcome_label", v)} testid="cs-outcome">
                <Field label="Outcome body" hint="What happened after release — reach, engagement, perception shift."><textarea rows={3} value={form.cs_outcome_body || ""} onChange={(e) => set("cs_outcome_body", e.target.value)} className={inputCls} placeholder="Followers grew from 200 to 2,000 in 45 days." /></Field>
              </SectionEditor>

              <SectionEditor visible={form.cs_closing_visible} onToggle={(v) => set("cs_closing_visible", v)} label={form.cs_closing_label} onLabelChange={(v) => set("cs_closing_label", v)} testid="cs-closing">
                <Field label="Closing line" hint="One reflective sentence."><textarea rows={2} value={form.cs_closing_body || ""} onChange={(e) => set("cs_closing_body", e.target.value)} className={inputCls} /></Field>
              </SectionEditor>
            </div>
          )}

          <Field label="Pull quote" hint="Optional. Shown in large gold italic on the case study page."><textarea rows={2} value={form.pull_quote || ""} onChange={(e) => set("pull_quote", e.target.value)} className={inputCls} placeholder="A film that doesn't shout." /></Field>
          <Field label="Pull quote — author" hint="E.g. 'Director, Elora' or 'Founder, Chill'"><input value={form.pull_quote_author || ""} onChange={(e) => set("pull_quote_author", e.target.value)} className={inputCls} /></Field>

          {!form.case_study_enabled && (
            <div className="pt-6 mt-6 border-t border-white/5">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <label className="overline block">Execution — videos &amp; images (stacked vertically)</label>
                  <p className="text-xs text-[#8A8A93] mt-2">Add videos (Vimeo URLs) and/or images. They appear in the order shown here, full width, with the optional caption below each.</p>
                </div>
              </div>
              <ExecutionEditor blocks={blocks} addBlock={addBlock} updateBlock={updateBlock} removeBlock={removeBlock} moveBlock={moveBlock} />
            </div>
          )}

          <Field label="Legacy gallery (one image URL per line)" hint="Optional. Renders as a simple stacked gallery below pull quote on the case study page."><textarea rows={3} value={galleryText} onChange={(e) => setGalleryText(e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Display order"><input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={inputCls} /></Field>
            <label className="flex items-center gap-3 pt-7">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C9A050]" />
              <span className="overline">Featured on home</span>
            </label>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" data-testid="work-save" className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all">Save</button>
            <button type="button" onClick={onCancel} className="overline border border-white/15 text-white hover:border-white/40 px-6 py-3">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#8A8A93] mt-2">{hint}</p>}
    </div>
  );
}


/* ---------- Helper editors ---------- */

function MetaRow({ form, set, field, defaultLabel, valueField, valueLabel, valueIsSelect }) {
  const visKey = `${field}_visible`;
  const labelKey = `${field}_label`;
  return (
    <div className="bg-white/[0.02] border border-white/5 p-4 space-y-3 mb-3" data-testid={`metarow-${field}`}>
      <div className="flex items-center justify-between gap-4">
        <input
          value={form[labelKey] ?? defaultLabel}
          onChange={(e) => set(labelKey, e.target.value)}
          placeholder={`Label (e.g. ${defaultLabel})`}
          data-testid={`metarow-${field}-label`}
          className={`${inputCls} max-w-[220px]`}
        />
        <label className="flex items-center gap-2 shrink-0">
          <input
            type="checkbox"
            checked={form[visKey] !== false}
            onChange={(e) => set(visKey, e.target.checked)}
            data-testid={`metarow-${field}-visible`}
            className="w-4 h-4 accent-[#C9A050]"
          />
          <span className="overline text-xs">Show</span>
        </label>
      </div>
      <Field label={valueLabel}>
        {valueIsSelect ? (
          <select value={form[valueField] || "Branding"} onChange={(e) => set(valueField, e.target.value)} className={inputCls}>
            {["Branding", "Film", "Social", "Digital"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <input value={form[valueField] || ""} onChange={(e) => set(valueField, e.target.value)} className={inputCls} />
        )}
      </Field>
    </div>
  );
}

function SectionEditor({ visible, onToggle, label, onLabelChange, testid, children }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-4">
        <input
          value={label || ""}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="Section label"
          data-testid={`${testid}-label`}
          className={`${inputCls} max-w-sm`}
        />
        <label className="flex items-center gap-2 shrink-0">
          <input
            type="checkbox"
            checked={visible !== false}
            onChange={(e) => onToggle(e.target.checked)}
            data-testid={`${testid}-visible`}
            className="w-4 h-4 accent-[#C9A050]"
          />
          <span className="overline text-xs">Show</span>
        </label>
      </div>
      <div className="space-y-3 pt-2">{children}</div>
    </div>
  );
}

function ExecutionEditor({ blocks, addBlock, updateBlock, removeBlock, moveBlock }) {
  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <p className="text-xs text-[#8A8A93] italic py-2">No execution blocks yet. Add a video, image, or audio track below.</p>
      )}
      {blocks.map((b, idx) => {
        const label = b.type === "video" ? "Video" : b.type === "audio" ? "♪ Audio" : "Image";
        return (
          <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={`exec-block-${idx}`}>
            <div className="flex items-center justify-between">
              <span className="overline text-[#C9A050]">{label} · {idx + 1}</span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => moveBlock(idx, -1)} data-testid={`exec-up-${idx}`} className="overline text-[#8A8A93] hover:text-white">↑</button>
                <button type="button" onClick={() => moveBlock(idx, 1)} data-testid={`exec-down-${idx}`} className="overline text-[#8A8A93] hover:text-white">↓</button>
                <button type="button" onClick={() => removeBlock(idx)} data-testid={`exec-remove-${idx}`} className="overline text-[#8A8A93] hover:text-red-400">Remove</button>
              </div>
            </div>
            {b.type === "audio" && (
              <input
                placeholder="Track title (e.g. Morning Rain — original composition)"
                value={b.title || ""}
                onChange={(e) => updateBlock(idx, "title", e.target.value)}
                data-testid={`exec-audio-title-${idx}`}
                className={inputCls}
              />
            )}
            <div className="space-y-2">
              <p className="overline text-[10px] text-[#8A8A93]">
                {b.type === "video" ? "Vimeo URL" : b.type === "audio" ? "Audio file (mp3/wav) or streaming URL" : "Image"}
              </p>
              <MediaInput
                value={b.url || ""}
                onChange={(v) => updateBlock(idx, "url", v)}
                accept={b.type === "video" ? "video" : b.type === "audio" ? "audio" : "image"}
                folder="works"
                testid={`exec-url-${idx}`}
                placeholder={b.type === "video" ? "Vimeo URL — https://player.vimeo.com/video/…" : b.type === "audio" ? "https://…/track.mp3" : "https://…"}
                compact
              />
            </div>
            <input
              placeholder="Caption (optional)"
              value={b.caption || ""}
              onChange={(e) => updateBlock(idx, "caption", e.target.value)}
              data-testid={`exec-caption-${idx}`}
              className={inputCls}
            />
          </div>
        );
      })}
      <div className="flex flex-wrap gap-3 mt-2">
        <button type="button" onClick={() => addBlock("video")} data-testid="exec-add-video" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add video</button>
        <button type="button" onClick={() => addBlock("image")} data-testid="exec-add-image" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add image</button>
        <button type="button" onClick={() => addBlock("audio")} data-testid="exec-add-audio" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add audio song</button>
      </div>
    </div>
  );
}

function KeyMomentsEditor({ moments, addMoment, updateMoment, removeMoment, moveMoment }) {
  return (
    <div className="space-y-3">
      {moments.length === 0 && (
        <p className="text-xs text-[#8A8A93] italic py-2">No key moments yet. Add 2–4 standout beats below.</p>
      )}
      {moments.map((m, idx) => (
        <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={`moment-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="overline text-[#C9A050]">Moment · {idx + 1}</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => moveMoment(idx, -1)} className="overline text-[#8A8A93] hover:text-white">↑</button>
              <button type="button" onClick={() => moveMoment(idx, 1)} className="overline text-[#8A8A93] hover:text-white">↓</button>
              <button type="button" onClick={() => removeMoment(idx)} data-testid={`moment-remove-${idx}`} className="overline text-[#8A8A93] hover:text-red-400">Remove</button>
            </div>
          </div>
          <input placeholder="Moment title (e.g. The reveal shot)" value={m.title || ""} onChange={(e) => updateMoment(idx, "title", e.target.value)} data-testid={`moment-title-${idx}`} className={inputCls} />
          <textarea rows={3} placeholder="Why this moment matters…" value={m.body || ""} onChange={(e) => updateMoment(idx, "body", e.target.value)} data-testid={`moment-body-${idx}`} className={inputCls} />
          <div className="flex items-center gap-3">
            <select value={m.media_type || "image"} onChange={(e) => updateMoment(idx, "media_type", e.target.value)} className={`${inputCls} max-w-[160px]`}>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="audio">♪ Audio song</option>
            </select>
            <input placeholder={m.media_type === "video" ? "Vimeo URL (optional)" : m.media_type === "audio" ? "Audio file URL (mp3/wav)" : "Image URL (optional)"} value={m.media_url || ""} onChange={(e) => updateMoment(idx, "media_url", e.target.value)} data-testid={`moment-media-${idx}`} className={inputCls} />
          </div>
        </div>
      ))}
      <button type="button" onClick={addMoment} data-testid="moment-add" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add moment</button>
    </div>
  );
}
